import ClassNames from "classnames";
import { FunctionComponent, useRef, useState } from "react";
import { RemoveScroll } from "react-remove-scroll";
import { CSSTransition } from "react-transition-group";
import tinycolors from "tinycolor2";
import headerBaseStyles from "../../stylesheets/components/header-base.module.css";
import styles from "../../stylesheets/components/header-mobile/header-4-and-5-mobile.module.css";
import imageOverlayStyles from "../../stylesheets/components/image-overlay.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import { CommonHeaderLayoutProps } from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import {
  useIntersection,
  useKeyDown,
  useScrollDirection,
} from "../../utils/hooks.js";
import {
  getEmailLink,
  getPhoneLink,
  getQuickLinksWithIcon,
  getWhatsAppLink,
  onTransitionEnd,
  overrideImagesAspectRatio,
} from "../../utils/utils.js";
import Icon from "../Icon.js";
import MainMenuListCollapsible, {
  verticalMenuClassNames,
} from "../MainMenuListCollapsible.js";
import ImagesModule from "../Modules/ImagesModule.js";
import SmartLink from "../SmartLink.js";
import HamburgerButton from "./HamburgerButton.js";
import MobileLanguageMenu from "./MobileLanguageMenu.js";
import MobileMainLogo from "./MobileMainLogo.js";
import QuickButtons from "./QuickButtons.js";

const Header4And5Mobile: FunctionComponent<CommonHeaderLayoutProps> = ({
  accommodation,
  actionLinks,
  activeModuleId,
  activePagePath,
  fallbackLanguageId,
  imageOverlayGradient: overlay,
  imagesModule: imagesModuleProp,
  isHomePage,
  isPreview,
  languageId,
  layoutVariant,
  logo,
  logoSize,
  mainPageURL,
  menuItems,
  pageId,
  pages,
  queries,
  scheme,
  sticky,
  whatsAppNumber,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const scrollDirection = useScrollDirection();
  const ref = useRef<HTMLDivElement>(null);
  const barNodeRef = useRef<HTMLDivElement>(null);
  const menuNodeRef = useRef<HTMLDivElement>(null);
  const intersection = useIntersection(ref);
  const isIntersecting = intersection?.isIntersecting ?? true;
  const imagesModule = imagesModuleProp
    ? overrideImagesAspectRatio(imagesModuleProp)
    : undefined;

  useKeyDown("Escape", () => setIsMenuOpen(false));

  const isFixed =
    !isPreview &&
    ((scrollDirection === "up" && !isIntersecting) || sticky || isMenuOpen);

  const showQuickButtons =
    isPreview ||
    isIntersecting ||
    sticky ||
    isMenuOpen ||
    scrollDirection === "up";

  const isHeaderBarOpaque =
    !isIntersecting || isMenuOpen || !imagesModule?.pageId;

  const quickNav = [
    getEmailLink(accommodation, languageId),
    ...(whatsAppNumber ? [getWhatsAppLink(whatsAppNumber)] : []),
    ...getQuickLinksWithIcon(actionLinks),
  ];

  const phoneLink = getPhoneLink(accommodation, languageId);

  return (
    <div
      className={ClassNames(headerBaseStyles["HeaderModule__Inner--narrow"], {
        [headerBaseStyles["HeaderModule__Inner--with-sidebar"]]:
          layoutVariant === "fixed-sidebar",
      })}
    >
      <header
        className={ClassNames(
          styles.Header4And5Mobile,
          styles[`Header4And5Mobile--height-${logoSize}`],
          {
            [styles["Header4And5Mobile--is-home"]]: isHomePage,
            [styles["Header4And5Mobile--preview"]]: isPreview,
            [styles["Header4And5Mobile--scrolled-down"]]: !isIntersecting,
            [styles["Header4And5Mobile--image-overlay-dark"]]:
              overlay === "dark",
            [styles["Header4And5Mobile--image-overlay-light"]]:
              overlay === "light",
            [styles["Header4And5Mobile--no-image"]]: !imagesModule?.pageId,
          },
          imageOverlayStyles.ImageOverlay__Header4And5Mobile,
          css({ background: scheme.main.background, color: scheme.main.text }),
        )}
      >
        <div
          ref={ref}
          className={ClassNames(styles.Header4And5Mobile__IntersectionHelper, {
            [styles["Header4And5Mobile__IntersectionHelper--scrolled-down"]]:
              !isIntersecting,
          })}
        />
        {imagesModule?.pageId && (
          <ImagesModule
            translatedModule={imagesModule}
            isPreview={isPreview}
            pageId={pageId}
            queries={queries}
            isActive={false}
            isInsideHeader={true}
            isFirstOnPage={false}
            parentScheme={scheme}
            activeModuleId={activeModuleId}
            fullHeight={true}
            className={styles.ImagesModule}
          />
        )}
        <CSSTransition
          timeout={300}
          in={isFixed}
          addEndListener={(done) => {
            barNodeRef.current && onTransitionEnd(barNodeRef.current, done);
          }}
          nodeRef={barNodeRef}
          classNames={{
            enter: styles["Header4And5Mobile__Bar--enter"],
            enterActive: styles["Header4And5Mobile__Bar--enter-active"],
            exit: ClassNames(
              styles["Header4And5Mobile__Bar--fixed"],
              styles["Header4And5Mobile__Bar--exit"],
            ),
            exitActive: ClassNames(
              styles["Header4And5Mobile__Bar--fixed"],
              styles["Header4And5Mobile__Bar--exit-active"],
            ),
          }}
        >
          <div
            ref={barNodeRef}
            className={ClassNames(styles.Header4And5Mobile__Bar, {
              [styles["Header4And5Mobile__Bar--fixed"]]: isFixed,
              [styles["Header4And5Mobile__Bar--transparent"]]:
                !isHeaderBarOpaque,
              [css({ background: scheme.main.background })]: isHeaderBarOpaque,
              [styles["Header4And5Mobile__Bar--scrolled-down"]]:
                !isIntersecting,
            })}
          >
            <div
              className={ClassNames(
                styles.Header4And5Mobile__BarInner,
                moduleBaseStyles.Module__Wrapper,
              )}
            >
              <div>
                <HamburgerButton
                  className={styles.Header4And5Mobile__HamburgerButton}
                  isMenuOpen={isMenuOpen}
                  languageId={languageId}
                  onClick={() => setIsMenuOpen((v) => !v)}
                />
              </div>
              <div className={styles.Header4And5Mobile__LogoContainer}>
                <MobileMainLogo
                  accommodationName={accommodation.name}
                  isPreview={isPreview}
                  logo={logo}
                  mainPageURL={mainPageURL}
                  isSmall={!isIntersecting}
                  size={logoSize}
                />
              </div>
              <div>
                <a
                  href={phoneLink.href}
                  className={ClassNames(
                    "BareBtn",
                    styles.Header4And5Mobile__Phone,
                  )}
                  title={phoneLink.title}
                >
                  <Icon glyph={phoneLink.icon} />
                </a>
              </div>
            </div>
          </div>
        </CSSTransition>

        <CSSTransition
          in={isMenuOpen}
          timeout={2000}
          mountOnEnter={true}
          unmountOnExit={true}
          addEndListener={(done) => {
            menuNodeRef.current && onTransitionEnd(menuNodeRef.current, done);
          }}
          nodeRef={menuNodeRef}
          classNames={{
            enterActive: styles["Header4And5Mobile__Menu--open"],
            enterDone: styles["Header4And5Mobile__Menu--open"],
          }}
        >
          <div
            ref={menuNodeRef}
            className={ClassNames(
              styles.Header4And5Mobile__Menu,
              css({
                background: tinycolors(scheme.main.background)
                  .darken(5)
                  .toRgbString(),
              }),
            )}
          >
            <RemoveScroll
              enabled={!isPreview}
              className={styles.Header4And5Mobile__MenuInner}
            >
              <MobileLanguageMenu
                className={styles.Header4And5Mobile__LanguageMenu}
                isPreview={isPreview}
                languageId={languageId}
                pageId={pageId}
                onClick={() => setIsMenuOpen(false)}
              />
              <MainMenuListCollapsible
                isPreview={isPreview}
                languageId={languageId}
                onMenuItemClick={() => setIsMenuOpen(false)}
                menuItems={menuItems}
                pages={pages}
                activePagePath={activePagePath}
                fallbackLanguageId={fallbackLanguageId}
                menuLevel={0}
                collapseLevel={0}
                classNames={verticalMenuClassNames}
              />
              <ul className={styles.Header4And5Mobile__QuickNav}>
                {quickNav.map(({ href, icon, type }) => (
                  <li key={type}>
                    <SmartLink
                      isPreview={isPreview}
                      anchorAttrs={{
                        href,
                        className: styles.Header4And5Mobile__QuickNavLink,
                        onClick: () => type !== "email" && setIsMenuOpen(false),
                      }}
                    >
                      <Icon
                        glyph={icon}
                        className={styles.Header4And5Mobile__QuickNavIcon}
                      />
                    </SmartLink>
                  </li>
                ))}
              </ul>
            </RemoveScroll>
          </div>
        </CSSTransition>
      </header>

      <QuickButtons
        actionLinks={actionLinks}
        className={css({
          background: scheme.primary.background,
          color: scheme.primary.text,
        })}
        isPreview={isPreview}
        isVisible={showQuickButtons}
        onClick={() => setIsMenuOpen(false)}
      />
    </div>
  );
};

export default Header4And5Mobile;
